import React, { useState, useMemo, useEffect } from 'react';
import './PreviousResultDrawer.css';

/**
 * Robust clinical delta and trend calculation helper.
 * Strictly respects parameter precision, handles qualitative results,
 * unit mismatches, zero baselines, and delta check thresholds.
 */
function calculateParameterDelta(currObs, prevObs, defaultThresholdPct = 20) {
  const currValRaw = currObs?.currentValue ?? currObs?.value ?? '';
  const prevValRaw = prevObs?.value ?? '';

  const currValStr = String(currValRaw).trim();
  const prevValStr = String(prevValRaw).trim();

  // Handle missing values
  if (!currValStr && !prevValStr) {
    return { isComparable: false, reason: 'NO_DATA', deltaStatus: 'NO DATA' };
  }
  if (!prevValStr) {
    return {
      isComparable: false,
      reason: 'NEW_PARAMETER',
      deltaStatus: 'NEW PARAMETER',
      trend: 'new',
      displayDelta: '—',
      displayPct: '—',
    };
  }
  if (!currValStr) {
    return {
      isComparable: false,
      reason: 'NOT_IN_CURRENT',
      deltaStatus: 'NOT IN CURRENT',
      trend: 'missing',
      displayDelta: '—',
      displayPct: '—',
    };
  }

  // Check unit compatibility
  const currUnit = (currObs?.unit || currObs?.readingFormat || '').trim().toLowerCase();
  const prevUnit = (prevObs?.readingFormat || prevObs?.unit || '').trim().toLowerCase();
  const isGenericUnit = (u) => !u || u === 'numeric' || u === 'option' || u === 'text';

  if (!isGenericUnit(currUnit) && !isGenericUnit(prevUnit) && currUnit !== prevUnit) {
    return {
      isComparable: false,
      reason: 'UNIT_MISMATCH',
      deltaStatus: 'NOT COMPARABLE',
      displayDelta: 'Unit Mismatch',
      displayPct: `${prevObs?.readingFormat || prevObs?.unit || '—'} → ${currObs?.unit || '—'}`,
      trend: 'incompatible',
    };
  }

  // Parse numeric values
  const currNum = parseFloat(currValStr);
  const prevNum = parseFloat(prevValStr);

  // If either is non-numeric, treat as qualitative comparison
  if (isNaN(currNum) || isNaN(prevNum)) {
    const isDifferent = currValStr.toLowerCase() !== prevValStr.toLowerCase();
    return {
      isComparable: true,
      isNumeric: false,
      currValStr,
      prevValStr,
      changed: isDifferent,
      trend: isDifferent ? 'changed' : 'stable',
      deltaStatus: isDifferent ? 'RESULT CHANGED' : 'STABLE',
      displayDelta: isDifferent ? 'Shifted' : 'Unchanged',
      displayPct: '—',
    };
  }

  // Numeric calculation
  const absDelta = currNum - prevNum;
  let pctChange = null;
  if (prevNum !== 0) {
    pctChange = ((currNum - prevNum) / Math.abs(prevNum)) * 100;
  }

  // Decimal precision: check explicit parameter property or detect from string
  let precision = 2;
  if (typeof currObs?.decimalPlaces === 'number') {
    precision = currObs.decimalPlaces;
  } else {
    const currDec = (currValStr.split('.')[1] || '').length;
    const prevDec = (prevValStr.split('.')[1] || '').length;
    precision = Math.min(3, Math.max(currDec, prevDec));
  }

  const sign = absDelta > 0 ? '+' : '';
  const formattedDelta = sign + absDelta.toFixed(precision);
  const formattedPct = pctChange !== null ? (pctChange > 0 ? '+' : '') + pctChange.toFixed(1) + '%' : 'N/A';

  // Trend direction (using small epsilon for floating precision)
  let trend = 'stable';
  if (absDelta > 0.0001) trend = 'increased';
  else if (absDelta < -0.0001) trend = 'decreased';

  // Configured or default delta check threshold
  const threshold = currObs?.deltaThresholdPercent || defaultThresholdPct;
  const isDeltaCheck = pctChange !== null && Math.abs(pctChange) >= threshold;
  const isSignificant = isDeltaCheck || (pctChange !== null && Math.abs(pctChange) >= 10);

  let deltaStatus = 'STABLE';
  if (isDeltaCheck) deltaStatus = 'DELTA CHECK';
  else if (isSignificant) deltaStatus = 'SIGNIFICANT CHANGE';
  else if (trend === 'increased') deltaStatus = 'INCREASED';
  else if (trend === 'decreased') deltaStatus = 'DECREASED';

  return {
    isComparable: true,
    isNumeric: true,
    currNum,
    prevNum,
    absDelta,
    pctChange,
    formattedDelta,
    formattedPct,
    trend,
    isDeltaCheck,
    isSignificant,
    deltaStatus,
    threshold,
  };
}

/**
 * Format reference range for display
 */
function getDisplayRefRange(obs) {
  if (obs.displayReading && obs.displayReading !== 'Numeric' && obs.displayReading !== 'Option') {
    return obs.displayReading;
  }
  const min = obs.minReading ?? obs.minValue;
  const max = obs.maxReading ?? obs.maxValue;
  if (min !== undefined && min !== null && max !== undefined && max !== null && (min !== '' || max !== '')) {
    return `${min} - ${max} ${obs.unit || obs.readingFormat || ''}`.trim();
  }
  return '—';
}

/**
 * Parse numeric min and max from reference range string for chart shading
 */
function parseRefRangeNumbers(obs) {
  const min = parseFloat(obs.minReading ?? obs.minValue);
  const max = parseFloat(obs.maxReading ?? obs.maxValue);
  if (!isNaN(min) && !isNaN(max)) return { min, max };

  if (obs.displayReading) {
    const parts = obs.displayReading.split('-');
    if (parts.length === 2) {
      const p0 = parseFloat(parts[0].trim());
      const p1 = parseFloat(parts[1].trim());
      if (!isNaN(p0) && !isNaN(p1)) return { min: p0, max: p1 };
    }
  }
  return null;
}

export const PreviousResultDrawer = ({
  isOpen,
  onClose,
  patient,
  tests = [],
  activeTestId = null,
  testObservationsMap = {},
  formDetailMap = {},
  historyItems = [],
  loading = false,
  onChangeTest = null,
}) => {
  // ── Active Test Selection ────────────────────────────────────────────────
  const activeOrderTests = useMemo(() => {
    if (tests && tests.length > 0) return tests;
    if (patient?.tests && patient.tests.length > 0) return patient.tests;
    return [];
  }, [tests, patient]);

  const [selectedTestId, setSelectedTestId] = useState(null);

  useEffect(() => {
    if (activeTestId) {
      setSelectedTestId(activeTestId);
    } else if (activeOrderTests.length > 0) {
      setSelectedTestId(activeOrderTests[0].testId);
    }
  }, [activeTestId, activeOrderTests]);

  // Current active test meta with formDetail fallback
  const currentTestMeta = useMemo(() => {
    const meta = activeOrderTests.find((t) => t.testId === selectedTestId) || activeOrderTests[0] || null;
    if (!meta) return null;
    const formMeta = formDetailMap?.[meta.testId];
    return {
      ...meta,
      investigationName: meta.investigationName || formMeta?.investigationName || meta.itemName || formMeta?.itemName || meta.itemCode || '',
      itemName: meta.itemName || formMeta?.itemName || meta.investigationName || formMeta?.investigationName || meta.itemCode || '',
    };
  }, [activeOrderTests, selectedTestId, formDetailMap]);

  // Current active test observations
  const currentObservations = useMemo(() => {
    if (!selectedTestId) return [];
    return testObservationsMap[selectedTestId] || [];
  }, [testObservationsMap, selectedTestId]);

  // ── Matching Historical Visits for Current Test ───────────────────────────
  const matchingPriorVisits = useMemo(() => {
    if (!historyItems || historyItems.length === 0) return [];

    const currentObsIds = new Set(
      (currentObservations || [])
        .map((o) => o.labObservationId)
        .filter(Boolean)
    );

    const testTitle = (
      currentTestMeta?.investigationName ||
      currentTestMeta?.itemName ||
      formDetailMap?.[selectedTestId]?.investigationName ||
      currentTestMeta?.itemCode ||
      ''
    ).toLowerCase().trim();

    const currentItemId = currentTestMeta?.itemId;

    return historyItems.filter((item) => {
      // 1. Definite match by shared observation IDs!
      if (currentObsIds.size > 0 && item.observations && item.observations.length > 0) {
        const overlap = item.observations.some((o) => currentObsIds.has(o.labObservationId));
        if (overlap) return true;
      }

      // 2. Match by ItemId if available
      if (currentItemId && item.itemId && item.itemId === currentItemId) return true;

      // 3. Name or Code match
      const priorName = (item.investigationName || item.itemName || '').toLowerCase().trim();
      if (priorName && testTitle) {
        if (priorName === testTitle) return true;
        if (testTitle.length >= 3 && priorName.includes(testTitle)) return true;
        if (priorName.length >= 3 && testTitle.includes(priorName)) return true;
        if (currentTestMeta?.itemCode) {
          const code = currentTestMeta.itemCode.toLowerCase();
          if (priorName.includes(`(${code})`) || priorName.includes(code)) return true;
        }
      }

      return false;
    });
  }, [currentObservations, currentTestMeta, historyItems, formDetailMap, selectedTestId]);

  // Selected prior visit ID to compare against (defaults to most recent)
  const [selectedPriorTestId, setSelectedPriorTestId] = useState(null);

  useEffect(() => {
    if (matchingPriorVisits.length > 0) {
      setSelectedPriorTestId(matchingPriorVisits[0].testId);
    } else {
      setSelectedPriorTestId(null);
    }
  }, [matchingPriorVisits]);

  const selectedPriorVisit = useMemo(() => {
    if (!selectedPriorTestId) return matchingPriorVisits[0] || null;
    return matchingPriorVisits.find((v) => v.testId === selectedPriorTestId) || matchingPriorVisits[0] || null;
  }, [matchingPriorVisits, selectedPriorTestId]);

  // ── UI Filters, Sorting, and Expansion States ────────────────────────────
  const [activeFilter, setActiveFilter] = useState('ALL'); // ALL, DELTA, CHANGED, ABNORMAL, STABLE
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedParamIds, setExpandedParamIds] = useState(new Set());

  const toggleExpand = (paramId) => {
    setExpandedParamIds((prev) => {
      const next = new Set(prev);
      if (next.has(paramId)) next.delete(paramId);
      else next.add(paramId);
      return next;
    });
  };

  // ── Parameter Comparison Records Matrix ──────────────────────────────────
  const comparisonRows = useMemo(() => {
    if (!currentObservations || currentObservations.length === 0) return [];

    const priorObsList = selectedPriorVisit?.observations || [];
    const priorLookup = new Map();
    priorObsList.forEach((o) => {
      if (o.labObservationId) priorLookup.set(o.labObservationId, o);
      if (o.labObservationName) priorLookup.set(o.labObservationName.toLowerCase().trim(), o);
    });

    // Collect ALL historical points across all prior visits for sparklines
    const historySeriesMap = new Map();
    matchingPriorVisits.forEach((visit) => {
      (visit.observations || []).forEach((obs) => {
        const key = obs.labObservationId || obs.labObservationName;
        if (!key) return;
        if (!historySeriesMap.has(key)) historySeriesMap.set(key, []);
        const numVal = parseFloat(obs.value);
        historySeriesMap.get(key).push({
          testId: visit.testId,
          labNo: visit.labNo,
          date: visit.date,
          value: obs.value,
          numericValue: isNaN(numVal) ? null : numVal,
          flag: obs.flag,
          isCritical: obs.isCritical,
          isCurrent: false,
        });
      });
    });

    return currentObservations.map((curr) => {
      const paramKey = curr.labObservationId || curr.name;
      const matchedPrior =
        priorLookup.get(curr.labObservationId) ||
        priorLookup.get((curr.name || '').toLowerCase().trim()) ||
        null;

      const delta = calculateParameterDelta(curr, matchedPrior);

      // Assemble time series for mini-sparkline (chronological: oldest to newest)
      const pastSeries = [...(historySeriesMap.get(paramKey) || [])].reverse();
      const currNum = parseFloat(curr.currentValue ?? curr.value);
      const fullSeries = [
        ...pastSeries,
        {
          testId: selectedTestId,
          labNo: patient?.ledgerTransactionNo || 'Current',
          date: patient?.sampleReceiveDate || new Date().toISOString(),
          value: curr.currentValue ?? curr.value ?? '—',
          numericValue: isNaN(currNum) ? null : currNum,
          flag: curr.currentFlag || curr.flag || 'N',
          isCritical: curr.isCritical,
          isCurrent: true,
        },
      ];

      const refRangeNumbers = parseRefRangeNumbers(curr);

      return {
        labObservationId: curr.labObservationId,
        name: curr.name || curr.labObservationName || 'Parameter',
        unit: curr.unit || curr.readingFormat || '',
        refRange: getDisplayRefRange(curr),
        refRangeNumbers,
        currentObs: curr,
        previousObs: matchedPrior,
        delta,
        timeSeries: fullSeries,
        isAbnormal: curr.currentFlag && curr.currentFlag !== 'N',
        isCritical: curr.isCritical || curr.currentFlag === 'LL' || curr.currentFlag === 'HH',
      };
    });
  }, [currentObservations, selectedPriorVisit, matchingPriorVisits, selectedTestId, patient]);

  // ── Scannable KPI Summary Calculations ───────────────────────────────────
  const summaryMetrics = useMemo(() => {
    let total = comparisonRows.length;
    let increased = 0;
    let decreased = 0;
    let stable = 0;
    let deltaAlerts = 0;
    let abnormalCount = 0;

    comparisonRows.forEach((row) => {
      if (row.delta.isDeltaCheck) deltaAlerts++;
      if (row.isAbnormal) abnormalCount++;

      if (row.delta.trend === 'increased') increased++;
      else if (row.delta.trend === 'decreased') decreased++;
      else if (row.delta.trend === 'stable') stable++;
    });

    return {
      total,
      increased,
      decreased,
      stable,
      deltaAlerts,
      abnormalCount,
      withinDelta: total - deltaAlerts,
    };
  }, [comparisonRows]);

  // ── "What Changed?" Automatic Objective Highlights ──────────────────────
  const whatChangedHighlights = useMemo(() => {
    const list = [];

    // 1. Delta check alerts
    comparisonRows
      .filter((r) => r.delta.isDeltaCheck)
      .forEach((r) => {
        list.push({
          type: 'alert',
          text: `⚠️ ${r.name} shifted by ${r.delta.formattedPct} (${r.delta.formattedDelta} ${r.unit}) from previous ${r.previousObs?.value} to ${r.currentObs?.currentValue ?? r.currentObs?.value}`,
        });
      });

    // 2. Newly abnormal parameters
    comparisonRows
      .filter((r) => !r.delta.isDeltaCheck && r.isAbnormal && r.previousObs && r.previousObs.flag === 'N')
      .slice(0, 3)
      .forEach((r) => {
        list.push({
          type: 'abnormal',
          text: `• ${r.name} moved into ${r.currentObs?.currentFlag || 'ABNORMAL'} range: ${r.previousObs.value} → ${r.currentObs?.currentValue ?? r.currentObs?.value} ${r.unit}`,
        });
      });

    // 3. Directional shifts
    comparisonRows
      .filter((r) => !r.delta.isDeltaCheck && r.delta.isSignificant && r.delta.isComparable)
      .slice(0, 3)
      .forEach((r) => {
        list.push({
          type: 'shift',
          text: `• ${r.name} ${r.delta.trend}: ${r.previousObs?.value} → ${r.currentObs?.currentValue ?? r.currentObs?.value} ${r.unit} (${r.delta.formattedPct})`,
        });
      });

    // 4. Stable note
    if (summaryMetrics.stable > 0) {
      list.push({
        type: 'stable',
        text: `• ${summaryMetrics.stable} parameter${summaryMetrics.stable === 1 ? '' : 's'} remained clinically stable within baseline variation`,
      });
    }

    if (list.length === 0) {
      list.push({
        type: 'none',
        text: 'All parameters match the previous baseline or are within normal physiological bounds.',
      });
    }

    return list;
  }, [comparisonRows, summaryMetrics]);

  // ── Filtered & Searched Rows ─────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    return comparisonRows.filter((row) => {
      // Filter tab
      if (activeFilter === 'DELTA' && !row.delta.isDeltaCheck) return false;
      if (activeFilter === 'CHANGED' && row.delta.trend === 'stable') return false;
      if (activeFilter === 'ABNORMAL' && !row.isAbnormal) return false;
      if (activeFilter === 'STABLE' && row.delta.trend !== 'stable') return false;

      // Text search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        return row.name.toLowerCase().includes(q);
      }
      return true;
    });
  }, [comparisonRows, activeFilter, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="ptd-drawer-backdrop" onClick={onClose}>
      <div className="ptd-drawer-panel" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="ptd-header">
          <div className="ptd-header-title-box">
            <div className="ptd-header-title-row">
              <h3>📜 Patient Trend & Parameter Delta Check</h3>
              <span className="ptd-badge-live">Real-time Comparative</span>
            </div>
            <div className="ptd-patient-demographics">
              <span className="ptd-demographics-item">
                Patient: <strong>{patient?.patientName || 'Unknown Patient'}</strong>
              </span>
              <span className="ptd-demographics-sep">•</span>
              <span className="ptd-demographics-item">
                UHID: <strong>{patient?.patientId || '—'}</strong>
              </span>
              <span className="ptd-demographics-sep">•</span>
              <span className="ptd-demographics-item">
                Current Lab: <strong>{patient?.ledgerTransactionNo || '—'}</strong>
              </span>
              {patient?.ageGender && (
                <>
                  <span className="ptd-demographics-sep">•</span>
                  <span className="ptd-demographics-item">{patient.ageGender}</span>
                </>
              )}
            </div>
          </div>
          <button type="button" className="ptd-btn-close" onClick={onClose} title="Close drawer (Esc)">
            ✕
          </button>
        </div>

        {/* ── Test Switcher Strip (Multi-Test Orders) ──────────────────────── */}
        {activeOrderTests.length > 1 && (
          <div className="ptd-test-tabs-bar">
            {activeOrderTests.map((t) => {
              const isActive = t.testId === selectedTestId;
              const obsCount = (testObservationsMap[t.testId] || []).length;
              return (
                <button
                  key={t.testId}
                  type="button"
                  className={`ptd-test-tab-btn ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTestId(t.testId);
                    onChangeTest?.(t.testId);
                  }}
                >
                  <span>🧪</span>
                  <span>{t.investigationName || t.itemName || formDetailMap?.[t.testId]?.investigationName || t.itemCode || `Test #${t.testId}`}</span>
                  {obsCount > 0 && <span className="ptd-tab-count-badge">{obsCount}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Drawer Scrollable Body ───────────────────────────────────────── */}
        <div className="ptd-drawer-body">
          {loading ? (
            <div className="ptd-empty-state">
              <div className="ptd-empty-icon">⏳</div>
              <div className="ptd-empty-title">Retrieving Patient Historical Records...</div>
              <div className="ptd-empty-desc">
                Loading prior laboratory investigations and correlating parameter baselines across previous encounters.
              </div>
            </div>
          ) : comparisonRows.length === 0 ? (
            <div className="ptd-empty-state">
              <div className="ptd-empty-icon">📂</div>
              <div className="ptd-empty-title">No Observation Parameters Loaded</div>
              <div className="ptd-empty-desc">
                {currentTestMeta
                  ? `No parameter values are currently recorded for ${currentTestMeta.itemName}.`
                  : 'Select an investigation to view parameter trend and delta comparisons.'}
              </div>
            </div>
          ) : (
            <>
              {/* 1. TOP SUMMARY CARD */}
              <div className="ptd-summary-card">
                <div className="ptd-summary-header">
                  <div className="ptd-test-title-box">
                    <span style={{ fontSize: '16px' }}>📊</span>
                    <span className="ptd-test-title">
                      {currentTestMeta?.investigationName || currentTestMeta?.itemName || 'INVESTIGATION PROFILE'}
                    </span>
                  </div>

                  {matchingPriorVisits.length > 1 && (
                    <div className="ptd-comparison-selector-box">
                      <span>Compare Baseline:</span>
                      <select
                        className="ptd-comparison-select"
                        value={selectedPriorTestId || ''}
                        onChange={(e) => setSelectedPriorTestId(parseInt(e.target.value, 10))}
                      >
                        {matchingPriorVisits.map((v, idx) => (
                          <option key={v.testId} value={v.testId}>
                            {idx === 0 ? '★ Most Recent: ' : ''}
                            {new Date(v.date).toLocaleDateString([], {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}{' '}
                            (Lab: {v.labNo})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Dates Comparison Strip */}
                <div className="ptd-dates-strip">
                  <div className="ptd-date-block previous">
                    <span className="ptd-date-label">PREVIOUS BASELINE</span>
                    <span className="ptd-date-val">
                      {selectedPriorVisit?.date ? (
                        new Date(selectedPriorVisit.date).toLocaleDateString([], {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      ) : (
                        <em style={{ color: '#94a3b8' }}>No Prior Visit</em>
                      )}
                    </span>
                    <span className="ptd-date-meta">
                      {selectedPriorVisit ? `Lab: ${selectedPriorVisit.labNo}` : 'First Recorded Finding'}
                    </span>
                  </div>

                  <div className="ptd-dates-arrow">→</div>

                  <div className="ptd-date-block current">
                    <span className="ptd-date-label">CURRENT ENCOUNTER</span>
                    <span className="ptd-date-val">
                      {new Date().toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="ptd-date-meta">Lab: {patient?.ledgerTransactionNo || 'Current'}</span>
                  </div>
                </div>

                {/* Compact Scannable KPI Chips */}
                <div className="ptd-kpi-chips-row">
                  <div className="ptd-kpi-chip chip-total">
                    <span>{summaryMetrics.total} Parameters</span>
                  </div>
                  <div className="ptd-kpi-chip chip-increased">
                    <span>↑ {summaryMetrics.increased} Increased</span>
                  </div>
                  <div className="ptd-kpi-chip chip-decreased">
                    <span>↓ {summaryMetrics.decreased} Decreased</span>
                  </div>
                  <div className="ptd-kpi-chip chip-stable">
                    <span>→ {summaryMetrics.stable} Stable</span>
                  </div>
                  {summaryMetrics.deltaAlerts > 0 ? (
                    <div className="ptd-kpi-chip chip-delta-alert">
                      <span>⚠ {summaryMetrics.deltaAlerts} Delta Check</span>
                    </div>
                  ) : (
                    <div className="ptd-kpi-chip chip-within-delta">
                      <span>✓ Within Delta</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 2. "WHAT CHANGED?" SUMMARY CALLOUT */}
              <div className="ptd-what-changed-box">
                <div className="ptd-what-changed-header">
                  <span>💡</span>
                  <span>What Changed Since Last Encounter?</span>
                </div>
                <ul className="ptd-what-changed-list">
                  {whatChangedHighlights.map((item, idx) => (
                    <li key={idx} className={`ptd-what-changed-item ${item.type === 'alert' ? 'item-alert' : ''}`}>
                      <span className="ptd-what-changed-bullet">•</span>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 3. FILTER CHIPS & SEARCH BAR */}
              <div className="ptd-filter-bar">
                <div className="ptd-filter-chips">
                  <button
                    type="button"
                    className={`ptd-filter-btn ${activeFilter === 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('ALL')}
                  >
                    <span>All</span>
                    <span className="badge">{summaryMetrics.total}</span>
                  </button>
                  {summaryMetrics.deltaAlerts > 0 && (
                    <button
                      type="button"
                      className={`ptd-filter-btn ${activeFilter === 'DELTA' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('DELTA')}
                    >
                      <span>⚠ Delta Check</span>
                      <span className="badge">{summaryMetrics.deltaAlerts}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className={`ptd-filter-btn ${activeFilter === 'CHANGED' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('CHANGED')}
                  >
                    <span>Changed</span>
                    <span className="badge">{summaryMetrics.increased + summaryMetrics.decreased}</span>
                  </button>
                  {summaryMetrics.abnormalCount > 0 && (
                    <button
                      type="button"
                      className={`ptd-filter-btn ${activeFilter === 'ABNORMAL' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('ABNORMAL')}
                    >
                      <span>Abnormal</span>
                      <span className="badge">{summaryMetrics.abnormalCount}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className={`ptd-filter-btn ${activeFilter === 'STABLE' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('STABLE')}
                  >
                    <span>Stable</span>
                    <span className="badge">{summaryMetrics.stable}</span>
                  </button>
                </div>

                <div className="ptd-search-box">
                  <span className="ptd-search-icon">🔍</span>
                  <input
                    type="text"
                    className="ptd-search-input"
                    placeholder="Search parameter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* 4. PARAMETER COMPARISON TABLE HEADER */}
              <div className="ptd-table-header">
                <div className="ptd-th-param">Parameter & Ref Interval</div>
                <div className="ptd-th-prev">Previous</div>
                <div className="ptd-th-curr">Current</div>
                <div className="ptd-th-delta">Delta / %</div>
                <div className="ptd-th-trend">Trend & Status</div>
                <div className="ptd-th-graph">Trend</div>
                <div></div>
              </div>

              {/* 5. PARAMETER-WISE COMPARISON ROWS */}
              <div className="ptd-comparison-rows-list">
                {filteredRows.map((row) => {
                  const isExpanded = expandedParamIds.has(row.labObservationId);
                  const delta = row.delta;
                  const currObs = row.currentObs;
                  const prevObs = row.previousObs;

                  const currValDisplay = currObs?.currentValue ?? currObs?.value ?? '—';
                  const prevValDisplay = prevObs?.value ?? '—';

                  return (
                    <div
                      key={row.labObservationId}
                      className={`ptd-param-row-card ${delta.isDeltaCheck ? 'card-delta-alert' : ''} ${
                        isExpanded ? 'card-expanded' : ''
                      }`}
                    >
                      {/* ── LEVEL 1: QUICK SCAN ROW ─────────────────────── */}
                      <div className="ptd-row-level1" onClick={() => toggleExpand(row.labObservationId)}>
                        {/* 1. Parameter & Range */}
                        <div className="ptd-cell-param">
                          <span className="ptd-param-name" title={row.name}>
                            {row.name}
                          </span>
                          <span className="ptd-param-ref-range">
                            Ref: {row.refRange}
                          </span>
                        </div>

                        {/* 2. Previous Value */}
                        <div className="ptd-cell-prev">
                          <span className="ptd-val-prev">{prevValDisplay}</span>
                          <span className="ptd-val-unit">{row.unit}</span>
                        </div>

                        {/* 3. Current Value */}
                        <div className="ptd-cell-curr">
                          <span className="ptd-val-curr">{currValDisplay}</span>
                          <span
                            className={`ptd-flag-badge flag-${(currObs?.currentFlag || currObs?.flag || 'n').toLowerCase()}`}
                          >
                            {currObs?.currentFlag || currObs?.flag || 'NORMAL'}
                          </span>
                        </div>

                        {/* 4. Delta & % Change */}
                        <div className="ptd-cell-delta">
                          {delta.isComparable && delta.isNumeric ? (
                            <>
                              <span
                                className={`ptd-delta-abs ${
                                  delta.absDelta > 0.0001
                                    ? 'positive'
                                    : delta.absDelta < -0.0001
                                    ? 'negative'
                                    : 'stable'
                                }`}
                              >
                                {delta.formattedDelta}
                              </span>
                              <span className="ptd-delta-pct">{delta.formattedPct}</span>
                            </>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              {delta.displayDelta || '—'}
                            </span>
                          )}
                        </div>

                        {/* 5. Trend & Status Badge */}
                        <div className="ptd-cell-trend">
                          <span className={`ptd-trend-indicator trend-${delta.trend}`}>
                            {delta.trend === 'increased' && '↑ Increased'}
                            {delta.trend === 'decreased' && '↓ Decreased'}
                            {delta.trend === 'stable' && '→ Stable'}
                            {delta.trend === 'changed' && '⇋ Shifted'}
                            {delta.trend === 'new' && '★ New'}
                            {delta.trend === 'incompatible' && '≠ Unit'}
                            {delta.trend === 'missing' && '— Absent'}
                          </span>
                          <span
                            className={`ptd-status-badge status-${delta.deltaStatus
                              .toLowerCase()
                              .replace(/\s+/g, '-')}`}
                          >
                            {delta.isDeltaCheck ? '⚠ DELTA CHECK' : delta.deltaStatus}
                          </span>
                        </div>

                        {/* 6. Mini Sparkline Graph */}
                        <div className="ptd-cell-graph">
                          <MiniSparkline timeSeries={row.timeSeries} refRange={row.refRangeNumbers} />
                        </div>

                        {/* Toggle Arrow */}
                        <div className={`ptd-toggle-arrow ${isExpanded ? 'open' : ''}`}>▼</div>
                      </div>

                      {/* ── LEVEL 2: DETAILED VIEW (ACCORDION) ──────────── */}
                      {isExpanded && (
                        <div className="ptd-row-level2">
                          <div className="ptd-level2-grid">
                            {/* Delta Explanation & Thresholds Box */}
                            <div className="ptd-level2-box">
                              <div className="ptd-level2-box-title">
                                <span>🔍</span>
                                <span>Delta Check & Shift Analysis</span>
                              </div>
                              <div className="ptd-delta-explanation">
                                <div className="ptd-delta-stat-row">
                                  <span className="ptd-delta-stat-label">Previous Baseline:</span>
                                  <span className="ptd-delta-stat-val">
                                    {prevValDisplay} {row.unit}{' '}
                                    {selectedPriorVisit?.date
                                      ? `(${new Date(selectedPriorVisit.date).toLocaleDateString()})`
                                      : ''}
                                  </span>
                                </div>
                                <div className="ptd-delta-stat-row">
                                  <span className="ptd-delta-stat-label">Current Finding:</span>
                                  <span className="ptd-delta-stat-val">
                                    {currValDisplay} {row.unit}
                                  </span>
                                </div>
                                {delta.isComparable && delta.isNumeric && (
                                  <>
                                    <div className="ptd-delta-stat-row">
                                      <span className="ptd-delta-stat-label">Absolute Difference:</span>
                                      <span
                                        className="ptd-delta-stat-val"
                                        style={{
                                          color:
                                            delta.absDelta > 0
                                              ? '#b45309'
                                              : delta.absDelta < 0
                                              ? '#0284c7'
                                              : '#334155',
                                        }}
                                      >
                                        {delta.formattedDelta} {row.unit}
                                      </span>
                                    </div>
                                    <div className="ptd-delta-stat-row">
                                      <span className="ptd-delta-stat-label">Percentage Shift:</span>
                                      <span
                                        className="ptd-delta-stat-val"
                                        style={{
                                          color: delta.isDeltaCheck ? '#be123c' : '#334155',
                                        }}
                                      >
                                        {delta.formattedPct}
                                      </span>
                                    </div>
                                    <div className="ptd-delta-stat-row">
                                      <span className="ptd-delta-stat-label">Configured Delta Threshold:</span>
                                      <span className="ptd-delta-stat-val">
                                        ±{delta.threshold || 20}.0%
                                      </span>
                                    </div>
                                  </>
                                )}
                                <div className="ptd-delta-stat-row">
                                  <span className="ptd-delta-stat-label">Clinical Delta Status:</span>
                                  <span
                                    className="ptd-delta-stat-val"
                                    style={{
                                      color: delta.isDeltaCheck ? '#be123c' : '#15803d',
                                    }}
                                  >
                                    {delta.isDeltaCheck
                                      ? '⚠ Delta Check Triggered (Shift exceeds threshold)'
                                      : delta.deltaStatus}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Chronological Encounter Points Table */}
                            <div className="ptd-level2-box">
                              <div className="ptd-level2-box-title">
                                <span>📅</span>
                                <span>Encounter History ({row.timeSeries.length} points)</span>
                              </div>
                              <table className="ptd-history-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Lab No</th>
                                    <th style={{ textAlign: 'right' }}>Result</th>
                                    <th style={{ textAlign: 'center' }}>Flag</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.timeSeries.map((pt, pIdx) => (
                                    <tr key={pIdx} className={pt.isCurrent ? 'current-row' : ''}>
                                      <td>
                                        {pt.isCurrent ? '★ Current' : new Date(pt.date).toLocaleDateString([], {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}
                                      </td>
                                      <td style={{ fontFamily: 'monospace' }}>{pt.labNo}</td>
                                      <td style={{ textAlign: 'right', fontWeight: '700' }}>
                                        {pt.value} {row.unit}
                                      </td>
                                      <td style={{ textAlign: 'center' }}>
                                        <span
                                          className={`ptd-flag-badge flag-${(pt.flag || 'n').toLowerCase()}`}
                                        >
                                          {pt.flag || 'N'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Expanded Visual Trend Chart with Reference Interval Shading */}
                          {row.timeSeries.filter((p) => p.numericValue !== null).length >= 2 && (
                            <div className="ptd-expanded-chart-wrapper">
                              <div className="ptd-level2-box-title">
                                <span>📈</span>
                                <span>Longitudinal Trend Graph with Reference Interval</span>
                              </div>
                              <ExpandedTrendChart
                                points={row.timeSeries.filter((p) => p.numericValue !== null)}
                                refRange={row.refRangeNumbers}
                                unit={row.unit}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="ptd-drawer-footer">
          <div className="ptd-footer-note">
            <span>🛡️</span>
            <span>CLSI EP28-A3 / ISO 15189 Delta Verification Engine Active</span>
          </div>
          <button type="button" className="ptd-btn-close-footer" onClick={onClose}>
            Close Drawer
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * High-performance inline SVG Sparkline
 */
const MiniSparkline = ({ timeSeries, refRange }) => {
  const numericPoints = useMemo(() => {
    return timeSeries.filter((p) => p.numericValue !== null && !isNaN(p.numericValue));
  }, [timeSeries]);

  if (numericPoints.length < 2) {
    return <span style={{ fontSize: '10px', color: '#cbd5e1' }}>—</span>;
  }

  const width = 80;
  const height = 24;
  const pad = 4;

  const vals = numericPoints.map((p) => p.numericValue);
  let min = Math.min(...vals);
  let max = Math.max(...vals);

  if (refRange) {
    min = Math.min(min, refRange.min);
    max = Math.max(max, refRange.max);
  }

  // Prevent divide-by-zero on flat line
  const range = max - min || 1;

  const getX = (idx) => pad + (idx / (numericPoints.length - 1)) * (width - 2 * pad);
  const getY = (val) => height - pad - ((val - min) / range) * (height - 2 * pad);

  const pointsStr = numericPoints.map((p, idx) => `${getX(idx)},${getY(p.numericValue)}`).join(' ');

  // Last point is current
  const lastIdx = numericPoints.length - 1;
  const lastX = getX(lastIdx);
  const lastY = getY(numericPoints[lastIdx].numericValue);
  const isLastAbnormal = numericPoints[lastIdx].flag && numericPoints[lastIdx].flag !== 'N';

  return (
    <svg className="ptd-sparkline-svg" viewBox={`0 0 ${width} ${height}`}>
      {/* Reference range shading if available */}
      {refRange && (
        <rect
          x={pad}
          y={Math.min(getY(refRange.min), getY(refRange.max))}
          width={width - 2 * pad}
          height={Math.abs(getY(refRange.max) - getY(refRange.min)) || 2}
          fill="#f0fdf4"
          opacity="0.8"
        />
      )}

      {/* Polyline */}
      <polyline
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pointsStr}
      />

      {/* Historical dots */}
      {numericPoints.slice(0, -1).map((p, idx) => (
        <circle
          key={idx}
          cx={getX(idx)}
          cy={getY(p.numericValue)}
          r="2"
          fill="#64748b"
        >
          <title>{`${new Date(p.date).toLocaleDateString()}: ${p.value}`}</title>
        </circle>
      ))}

      {/* Current dot */}
      <circle
        cx={lastX}
        cy={lastY}
        r="3.5"
        fill={isLastAbnormal ? '#e11d48' : '#2563eb'}
        stroke="#ffffff"
        strokeWidth="1"
      >
        <title>{`Current: ${numericPoints[lastIdx].value}`}</title>
      </circle>
    </svg>
  );
};

/**
 * Expanded High-Resolution SVG Trend Chart with Reference Interval Shading
 */
const ExpandedTrendChart = ({ points, refRange, unit }) => {
  const width = 640;
  const height = 130;
  const pad = { top: 16, right: 30, bottom: 26, left: 45 };

  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const vals = points.map((p) => p.numericValue);
  let min = Math.min(...vals);
  let max = Math.max(...vals);

  if (refRange) {
    min = Math.min(min, refRange.min);
    max = Math.max(max, refRange.max);
  }

  // Add 10% breathing space
  const span = max - min || 1;
  const yMin = min - span * 0.1;
  const yMax = max + span * 0.1;
  const yRange = yMax - yMin;

  const getX = (idx) => pad.left + (idx / Math.max(1, points.length - 1)) * plotW;
  const getY = (val) => pad.top + plotH - ((val - yMin) / yRange) * plotH;

  const pointsStr = points.map((p, idx) => `${getX(idx)},${getY(p.numericValue)}`).join(' ');

  return (
    <svg className="ptd-expanded-chart-svg" viewBox={`0 0 ${width} ${height}`}>
      {/* Reference interval band */}
      {refRange && (
        <g>
          <rect
            x={pad.left}
            y={getY(refRange.max)}
            width={plotW}
            height={Math.max(2, getY(refRange.min) - getY(refRange.max))}
            fill="#dcfce7"
            opacity="0.6"
          />
          <line
            x1={pad.left}
            y1={getY(refRange.max)}
            x2={pad.left + plotW}
            y2={getY(refRange.max)}
            stroke="#86efac"
            strokeDasharray="3 3"
          />
          <line
            x1={pad.left}
            y1={getY(refRange.min)}
            x2={pad.left + plotW}
            y2={getY(refRange.min)}
            stroke="#86efac"
            strokeDasharray="3 3"
          />
          <text
            x={pad.left + 6}
            y={getY(refRange.max) - 3}
            fontSize="9"
            fill="#15803d"
            fontWeight="600"
          >
            Ref Upper ({refRange.max})
          </text>
        </g>
      )}

      {/* Grid line at bottom */}
      <line
        x1={pad.left}
        y1={pad.top + plotH}
        x2={pad.left + plotW}
        y2={pad.top + plotH}
        stroke="#cbd5e1"
      />

      {/* Trend line */}
      <polyline
        fill="none"
        stroke="#2563eb"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pointsStr}
      />

      {/* Dots and Labels */}
      {points.map((p, idx) => {
        const cx = getX(idx);
        const cy = getY(p.numericValue);
        const isCurrent = p.isCurrent;
        const isAbnormal = p.flag && p.flag !== 'N';

        return (
          <g key={idx}>
            <circle
              cx={cx}
              cy={cy}
              r={isCurrent ? 5 : 3.5}
              fill={isAbnormal ? '#e11d48' : '#2563eb'}
              stroke="#ffffff"
              strokeWidth="1.5"
            />
            {/* Value text above dot */}
            <text
              x={cx}
              y={cy - 8}
              textAnchor="middle"
              fontSize="10"
              fontWeight={isCurrent ? '700' : '600'}
              fill={isCurrent ? '#1d4ed8' : '#334155'}
            >
              {p.value}
            </text>
            {/* Date text below axis */}
            <text
              x={cx}
              y={pad.top + plotH + 16}
              textAnchor="middle"
              fontSize="9"
              fill="#64748b"
            >
              {isCurrent
                ? 'Current'
                : new Date(p.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default PreviousResultDrawer;
